import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const DEMO_ACCOUNTS = [
  { label: "Super Admin", email: "nightouttts@gmail.com" },
  { label: "Admin", email: "admin@wellora.demo" },
  { label: "Sales Manager", email: "sales@wellora.demo" },
  { label: "Customer Care", email: "care@wellora.demo" },
];

export default function Login() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
    } else {
      toast.success("Welcome back to Wellora AI");
    }
  };

  const quickFill = (mail) => {
    setEmail(mail);
    setPassword("Wellora@123");
    setError("");
  };

  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-slate-50" data-testid="login-page">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-emerald-700 p-12 text-white">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <div className="rounded-lg bg-white px-3 py-2">
            <img src="/wellora-logo.jpeg" alt="Wellora AI" className="h-8 w-auto" />
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-4xl font-semibold leading-tight tracking-tight">
            The AI-powered healthcare CRM built to capture, convert & grow.
          </h1>
          <p className="mt-5 text-emerald-50/90 text-base leading-relaxed">
            Unify patient conversations, leads, appointments and follow-ups in one
            calm, intelligent workspace — designed for modern clinics and healthcare groups.
          </p>
          <div className="mt-10 flex items-center gap-2 text-sm text-emerald-50/80">
            <ShieldCheck className="h-4 w-4" />
            Enterprise-grade, HIPAA-minded architecture
          </div>
        </div>
        <div className="relative z-10 text-xs uppercase tracking-[0.2em] text-emerald-100/70">
          Capture • Convert • Grow
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden mb-8 flex justify-center">
            <img src="/wellora-logo.jpeg" alt="Wellora AI" className="h-10 w-auto" />
          </div>

          <div className="mb-8">
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">
              Sign in to your workspace
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Enter your credentials to access the Wellora AI console.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email address</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@clinic.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="h-11"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600" data-testid="login-error">{error}</p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="login-submit-button"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</>
              ) : (
                <>Sign in <ArrowRight className="h-4 w-4" /></>
              )}
            </Button>
          </form>

          <div className="mt-8 rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">
              Demo accounts (password: Wellora@123)
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {DEMO_ACCOUNTS.map((acc) => (
                <button
                  key={acc.email}
                  type="button"
                  onClick={() => quickFill(acc.email)}
                  className="rounded-md border border-slate-200 px-3 py-2 text-left text-xs text-slate-600 transition-colors hover:border-emerald-300 hover:bg-emerald-50"
                  data-testid={`demo-fill-${acc.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="block font-medium text-slate-800">{acc.label}</span>
                  <span className="block truncate text-slate-400">{acc.email}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
